import React, { useEffect, useState } from 'react';
import styles from './DiscourseForm.module.scss';
import Text from '../../../../Components/Base/Text/Text';
import SavingSpinner from '../../../../Components/Base/Loading/SavingSpinner';
import FormControl from '../../../../Components/Form/FormControl';
import DiagnosisSelectField from '../../../../Components/Base/Diagnosis/DiagnosisSelectField';
import TextArea from '../../../../Components/Base/TextArea/TextArea';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import RemoveButton from '../../../../Components/Button/RemoveButton';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import DiscourseAttachments from '../DiscourseAttachments/DiscourseAttachments';
import {
	DiscourseValues,
	useDiscourseContext,
} from '../../Services/DiscourseContext';
import FormMultiplierRemoveButton from '../../../../Components/Form/FormMultiplierRemoveButton';
import FormMultiplier from '../../../../Components/Form/FormMultiplier';
import { isEqual, cloneDeep } from 'lodash';
import InputField from '../../../../Components/Form/components/InputField';
import Loading from '../../../../Components/Loading';
import { Position } from '../../../Base/Enums/Position.enum';
import LinkButtonWithIcon from '../../../../Components/Button/LinkButtonWithIcon';
import PredefinedPhrasesDiscourseSelectionModal from '../../../PredefinedPhrases/Components/PredefinedPhrasesDiscourse/PredefinedPhrasesDiscourseSelection/PredefinedPhrasesDiscourseSelectionModal';
import { PredefineDiscoursePhrase } from '../../../PredefinedPhrases/Components/PredefinedPhrasesDiscourse/PredefinedPhrasesDiscourseSetup';
import {
	getDiscourseSickLeaveDocumentId,
	getSickLeaveTreatmentUrl,
	hasDiscourseMainDiagnosis,
	hasDiscourseSickLeave,
	prepareDefaultValues,
} from '../../Services/discourse.service';
import { Document, DocumentItem } from '../../../../graphql/graphql';
import { useAclContext } from '../../../Acl/Components/AclContextProvider';
import DiscourseAttachDocumentModal from '../DiscourseAttachDocument/DiscourseAttachDocumentModal';
import DiscourseTravelModal from '../DiscourseTravel/DiscourseTravelModal';
import DiscourseTravelItem from '../DiscourseTravel/DiscourseTravelItem';
import moment from 'moment';
import DateField from '../../../../Components/Form/components/DateField';
import TimePickerField from '../../../../Components/Form/components/TimePickerField/TimePickerField';
import DiscourseMedicalProceduresList from '../DiscourseMedicalProceduresList/DiscourseMedicalProceduresList';
import useScope from '../../../Scope/Hooks/useScope';
import { NCZI_OID } from '../../../Nczi/Enums/ncziOid.enum';
import NCZIReferenceSearchSelectField from '../../../Nczi/Components/ReferenceSearchSelect/NCZIReferenceSearchSelectField';
import DiscourseActions from '../DiscourseActions/DiscourseActions';
import CSSZSickLeaveCertificateCreateModal from '../../../CSSZSickLeaveCertificate/Components/CSSZSickLeaveCertificateCreate/CSSZSickLeaveCertificateCreateModal';
import { CSSZUser } from '../../../CSSZSickLeaveCertificate/Interfaces/CSSZ.interface';
import { useCsszSickLeaveUserQuery } from '../../../../graphql/graphql';
import { useDisclose } from '../../../Modal/Hooks/useDisclose';

interface Props {
	patientId: string;
	openCareClaimModal: (
		discourseId: string,
		discourseMainDiagnosis?: DocumentItem,
	) => void;
	virtualPatientId: string;
	careClaimsListRefreshVersion?: number;
	showCreateSickLeaveButton?: boolean;
	onSaveSuccess?: () => void;
}

interface DiagnosisComponentProps {
	name: string;
	label: string;
}

const DiagnosisComponent: React.FC<DiagnosisComponentProps> = ({
	name,
	label,
}) => {
	const { isSlovakia } = useScope();

	if (isSlovakia) {
		return (
			<NCZIReferenceSearchSelectField
				name={name}
				label={label}
				oid={NCZI_OID.DIAGNOSIS}
				showCode
				isRequired
			/>
		);
	}
	return <DiagnosisSelectField name={name} label={label} />;
};

const DiscourseForm: React.FC<Props> = ({
	patientId,
	openCareClaimModal,
	virtualPatientId,
	careClaimsListRefreshVersion,
	showCreateSickLeaveButton = false,
	onSaveSuccess,
}) => {
	const {
		isDiscourseSaving,
		draftDiscourseValues,
		handleSaveDiscourse,
		activeDiscourseId,
		handleDiscardActiveDiscourse,
		setDiscourseValues,
		discourseValues,
		discourseAttachments,
		handleRefreshAttachments,
	} = useDiscourseContext();
	const { isAllowed } = useAclContext();
	const {
		isOpen: isSickLeaveCreateOpen,
		onOpen: onOpenSickLeaveCreate,
		onClose: onCloseSickLeaveCreate,
	} = useDisclose();
	const { data: sickLeaveUserData, isLoading: isSickLeaveUserLoading } =
		useCsszSickLeaveUserQuery(
			{ id: virtualPatientId },
			{ skip: !showCreateSickLeaveButton },
		);

	if (!activeDiscourseId) {
		return <Loading />;
	}

	const [isPredefinedPhraseSelectionOpen, setIsPredefinedPhraseSelectionOpen] =
		useState(false);
	const [isAttachDocumentOpen, setIsAttachDocumentOpen] = useState(false);
	const [isAttachTravelOpen, setIsAttachTravelOpen] = useState(false);

	const handleMergeText = (text: string) => {
		return formValues.text ? `${formValues.text}\n${text}` : text;
	};

	const handleMergeSecondaryDiagnoses = (
		diagnoses: DiscourseValues['diagnoses'],
	) => {
		const formDiagnoses =
			formValues.diagnoses.length > 0 &&
			(formValues.diagnoses[0] as any)?.secondaryDiagnosis
				? formValues.diagnoses
				: [];

		return [...formDiagnoses, ...(diagnoses ?? [])];
	};

	const handleLoadFromPredefinedPhrase = (item: PredefineDiscoursePhrase) => {
		const predefinedValues = prepareDefaultValues(item as Document);
		formMethods.setValue('name', predefinedValues.name);
		formMethods.setValue('date', predefinedValues.date);
		formMethods.setValue('time', predefinedValues.time);
		formMethods.setValue('text', handleMergeText(predefinedValues.text));
		formMethods.setValue('diagnosis', predefinedValues.diagnosis);
		formMethods.setValue(
			'diagnoses',
			handleMergeSecondaryDiagnoses(predefinedValues.diagnoses),
		);
		setIsPredefinedPhraseSelectionOpen(false);
	};

	const defaultData = {
		name: '',
		date: moment().toISOString(),
		time: moment(),
		isDatetimeManual: false,
		text: '',
		diagnosis: undefined,
		diagnoses: [{}],
		discourseId: activeDiscourseId,
	};

	const formMethods = useForm({
		defaultValues: cloneDeep(defaultData),
	});

	const formValues = formMethods.watch();

	const canEditCareClaims = isAllowed('document_care_claim');

	const showCreateSickLeave =
		showCreateSickLeaveButton &&
		hasDiscourseMainDiagnosis(formValues.diagnosis) &&
		!hasDiscourseSickLeave(discourseAttachments);

	const discourseSickLeaveDocumentId =
		getDiscourseSickLeaveDocumentId(discourseAttachments);

	const sickLeaveUrl = discourseSickLeaveDocumentId
		? getSickLeaveTreatmentUrl(virtualPatientId, discourseSickLeaveDocumentId)
		: undefined;

	const handleSickLeaveCreate = async () => {
		await handleRefreshAttachments(true);
		onCloseSickLeaveCreate();
	};

	useEffect(() => {
		setDiscourseValues(cloneDeep(defaultData));
		formMethods.reset(cloneDeep(defaultData));
	}, [activeDiscourseId]);

	useEffect(() => {
		if (
			draftDiscourseValues?.diagnoses &&
			!draftDiscourseValues?.diagnoses?.length
		) {
			draftDiscourseValues['diagnoses'] = [{}];
		}
		formMethods.reset(draftDiscourseValues);
	}, [draftDiscourseValues]);

	useEffect(() => {
		const subscription = formMethods.watch((values, { name, type }) => {
			if (
				type === 'change' &&
				(name === 'date' || name === 'time') &&
				!values.isDatetimeManual
			) {
				formMethods.setValue('isDatetimeManual', true);
			}
		});
		return () => subscription.unsubscribe();
	}, [formMethods]);

	useEffect(() => {
		if (!isEqual(discourseValues, formValues)) {
			setDiscourseValues(cloneDeep(formValues) as DiscourseValues);
		}
	}, [formValues]);

	return (
		<div className={styles.wrapper}>
			<div className={styles.header}>
				<Text
					tx={'base.patientDekurz'}
					className={styles.title}
					firstUpper={true}
				/>
				{isDiscourseSaving && <SavingSpinner />}
			</div>
			<FormWrapper formMethods={formMethods}>
				<div className={'row'}>
					<div className={'col-6 mb-2'}>
						<InputField name={'name'} label={'base.shared.name'} />
					</div>
					<div className={'col-3 mb-2'}>
						<DateField name={'date'} label={'base.shared.date'} isRequired />
					</div>
					<div className={'col-3 mb-2'}>
						<TimePickerField
							name={'time'}
							label={'base.shared.time'}
							isRequired
						/>
					</div>
				</div>
				<div className={'row'}>
					<div className={'col-6 mb-2'}>
						<DiagnosisComponent
							name='diagnosis'
							label='component.ePrescription.mainDiagnosis'
						/>
					</div>
					<div className={'col-6 mb-2'}>
						<FormMultiplier
							name='diagnoses'
							addButtonTitle='base.addAnother'
							createDefault={1}>
							<DiagnosisComponent
								name='secondaryDiagnosis'
								label='component.ePrescription.secondaryDiagnosis'
							/>
							<FormMultiplierRemoveButton>
								<RemoveButton
									onClick={() => void 0}
									title='base.shared.remove'
								/>
							</FormMultiplierRemoveButton>
						</FormMultiplier>
					</div>
				</div>
				<div style={{ display: 'none' }}>
					<InputField name='discourseId' />
				</div>
				<FormControl name='text' hideRequired={true}>
					<TextArea
						rows={8}
						className={styles.textarea}
						captureAudio={{ allow: true, position: Position.RIGHT }}
					/>
				</FormControl>
				<DiscourseTravelItem onEdit={() => setIsAttachTravelOpen(true)} />
				<DiscourseAttachments
					patientId={patientId}
					virtualPatientId={virtualPatientId}
				/>
				<DiscourseMedicalProceduresList
					discourseId={activeDiscourseId}
					careClaimsListRefreshVersion={careClaimsListRefreshVersion}
				/>
				<div
					className={`${styles.footer} d-flex flex-row justify-content-between align-items-center`}>
					<div className={'d-flex flex-row align-items-center'}>
						<SubmitButton
							onSubmit={async () => {
								if (await handleSaveDiscourse()) {
									onSaveSuccess?.();
								}
							}}
							title={'base.shared.save'}
							disabled={!activeDiscourseId || isDiscourseSaving}
						/>
						<div className={'ml-3'}>
							{activeDiscourseId && (
								<RemoveButton
									onClick={handleDiscardActiveDiscourse}
									title='component.discourse.deleteButton'
								/>
							)}
						</div>
					</div>
					<div
						className={`${styles.actionsWrapper} d-flex flex-row align-items-center flex-wrap`}>
						<LinkButtonWithIcon
							title='base.predefinedPhrases'
							iconPosition={Position.LEFT}
							icon='/dist/icons/react/chat-lines-blue.svg'
							onClick={() => setIsPredefinedPhraseSelectionOpen(true)}
						/>
						<DiscourseActions
							onConnectDocument={() => setIsAttachDocumentOpen(true)}
							onConnectTravel={() => setIsAttachTravelOpen(true)}
							showCreateSickLeave={showCreateSickLeave}
							sickLeaveCreateDisabled={
								isSickLeaveUserLoading || !sickLeaveUserData?.user
							}
							onOpenSickLeaveCreate={onOpenSickLeaveCreate}
							sickLeaveUrl={sickLeaveUrl}
							canEditCareClaims={canEditCareClaims}
							activeDiscourseId={activeDiscourseId}
							mainDiagnosis={formValues.diagnosis}
							openCareClaimModal={openCareClaimModal}
						/>
					</div>
					{isAttachDocumentOpen && (
						<DiscourseAttachDocumentModal
							isOpen
							patientId={virtualPatientId}
							onClose={() => setIsAttachDocumentOpen(false)}
						/>
					)}
					{isAttachTravelOpen && (
						<DiscourseTravelModal
							isOpen
							patientId={virtualPatientId}
							onClose={() => setIsAttachTravelOpen(false)}
						/>
					)}
					{isPredefinedPhraseSelectionOpen && (
						<PredefinedPhrasesDiscourseSelectionModal
							isOpen
							onClose={() => setIsPredefinedPhraseSelectionOpen(false)}
							onSelect={handleLoadFromPredefinedPhrase}
						/>
					)}
					{showCreateSickLeave &&
						isSickLeaveCreateOpen &&
						sickLeaveUserData?.user && (
							<CSSZSickLeaveCertificateCreateModal
								isOpen
								onClose={onCloseSickLeaveCreate}
								onCreate={handleSickLeaveCreate}
								user={sickLeaveUserData.user as CSSZUser}
								discourseDocumentId={activeDiscourseId}
							/>
						)}
				</div>
			</FormWrapper>
		</div>
	);
};

export default DiscourseForm;
